import React, { useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import {
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledButtonDropdown,
} from "reactstrap";
import __ from "src/utils/Translations";
import { downloadFile, getFullFileName } from "src/utils/Api";
import useHasPermission from "src/utils/hooks/security/useHasPermission";
import DropdownSecurityWrapper from "src/Components/DataTableControlled/DropdownSecurityWrapper";
import {
  buildExportPath,
  getDropdownItems,
} from "src/Components/DataTableControlled/utils";
import { MAX_EXPORT_ITEMS } from "src/Components/DataTableControlled/consts";
import { useCustomExportInFlight } from "./useCustomExportInFlight";

const ExportButton = ({
  filters,
  path,
  additionalPath,
  getIsAdditionalPathVisible,
  page,
  pageSize,
  service,
  count,
  totalCount,
  sort,
  defaultSort,
  permissions,
  fileName,
  handleAdditionalFilters,
  noResults,
  hideIfNoPermission,
  buttonTitle,
  customDropdownItems,
  customExportHandler,
  withoutPermission,
}) => {
  const hasPermissions = useHasPermission(permissions);
  const { isCustomExporting, handleCustomExport } = useCustomExportInFlight(
    customExportHandler,
    fileName
  );

  const getPath = useCallback(
    (size) =>
      typeof getIsAdditionalPathVisible === "function" &&
      getIsAdditionalPathVisible(size)
        ? additionalPath
        : path,
    [additionalPath, path, getIsAdditionalPathVisible]
  );

  const pathForMaxExportItems = useMemo(
    () => getPath(totalCount || MAX_EXPORT_ITEMS),
    [totalCount, getPath]
  );
  const pathForPageSizeItems = useMemo(
    () => getPath(pageSize),
    [pageSize, getPath]
  );
  const pathForCustomSizeItems = useMemo(
    () => getPath(count),
    [count, getPath]
  );

  if (hideIfNoPermission && !hasPermissions && !withoutPermission) return null;

  return (
    <UncontrolledButtonDropdown size="lg" className="me-2">
      <DropdownSecurityWrapper
        withoutPermission={withoutPermission}
        permissions={permissions}
      >
        <DropdownToggle
          caret
          color="secondary"
          aria-busy={customExportHandler ? isCustomExporting : undefined}
        >
          <span data-t1="export">{__(buttonTitle)}</span>
        </DropdownToggle>
      </DropdownSecurityWrapper>
      <DropdownMenu strategy="fixed">
        {customDropdownItems.length
          ? customDropdownItems.map(
              (
                {
                  title,
                  dataT1,
                  fileType,
                  pageSize: customPageSize = MAX_EXPORT_ITEMS,
                },
                index
              ) => (
                <DropdownItem
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${title}-${index}`}
                  disabled={Boolean(customExportHandler && isCustomExporting)}
                  onClick={
                    customExportHandler
                      ? () => handleCustomExport({ fileType })
                      : async () =>
                          downloadFile(
                            service,
                            await buildExportPath(
                              pathForPageSizeItems,
                              [],
                              handleAdditionalFilters,
                              sort,
                              customPageSize,
                              null,
                              defaultSort
                            ),
                            getFullFileName(fileName, true, fileType)
                          )
                  }
                >
                  <span data-t1={dataT1}>{__(title)}</span>
                </DropdownItem>
              )
            )
          : getDropdownItems({
              noResults,
              service,
              filters,
              sort,
              defaultSort,
              pageSize,
              page,
              fileName,
              pathForPageSizeItems,
              pathForCustomSizeItems,
              pathForMaxExportItems,
              handleAdditionalFilters,
            })}
      </DropdownMenu>
    </UncontrolledButtonDropdown>
  );
};

// The component supports two mutually exclusive export modes:
//   1. Legacy REST download — requires both `service` and `path`.
//   2. Custom handler — requires `customExportHandler`; `service`/`path` unused.
// This validator enforces that exactly one mode is properly configured instead
// of forcing callers to pass empty strings to satisfy required props.
const requiredUnlessCustomHandler =
  (propName) => (props, name, componentName) => {
    if (props.customExportHandler) return null;
    const value = props[propName];
    if (typeof value === "string" && value.length > 0) return null;
    return new Error(
      `The prop \`${propName}\` is required in \`${componentName}\` when ` +
        "`customExportHandler` is not provided, and must be a non-empty string. " +
        `Received: ${
          value === undefined ? "undefined" : `\`${String(value)}\``
        }.`
    );
  };

ExportButton.propTypes = {
  service: requiredUnlessCustomHandler("service"),
  path: requiredUnlessCustomHandler("path"),
  additionalPath: PropTypes.string,
  getIsAdditionalPathVisible: PropTypes.func,
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ).isRequired,
  count: PropTypes.number,
  totalCount: PropTypes.number,
  page: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  sort: PropTypes.shape({
    key: PropTypes.string,
    value: PropTypes.string,
  }),
  defaultSort: PropTypes.oneOfType([
    PropTypes.shape({
      key: PropTypes.string,
      value: PropTypes.string,
    }),
    PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string,
        value: PropTypes.string,
      })
    ),
  ]),
  fileName: PropTypes.string.isRequired,
  noResults: PropTypes.bool,
  permissions: PropTypes.string,
  handleAdditionalFilters: PropTypes.func,
  hideIfNoPermission: PropTypes.bool,
  buttonTitle: PropTypes.string,
  customDropdownItems: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      dataT1: PropTypes.string,
      fileType: PropTypes.string,
    })
  ),
  customExportHandler: PropTypes.func,
  withoutPermission: PropTypes.bool,
};

ExportButton.defaultProps = {
  service: undefined,
  path: undefined,
  additionalPath: "",
  getIsAdditionalPathVisible: null,
  permissions: null,
  handleAdditionalFilters: null,
  noResults: false,
  count: 0,
  totalCount: 0,
  sort: null,
  defaultSort: null,
  hideIfNoPermission: false,
  buttonTitle: "Eksportuj",
  customDropdownItems: [],
  customExportHandler: null,
  withoutPermission: false,
};
export default ExportButton;
